import { graphicApi } from '@/api';
import { ArchiveModal } from '@/components/badge';
import { WithNewGraphicModal } from '@/components/common';
import { getDefaultPaginationSettings, getEnumNameFromValue, handleResponse, removeCommasAndPercentSign } from '@/Helper';
import { OperatorsDetail, PaginationSetting, ResponseObjectEntity, SortDirections, ViewType } from '@/model/CommonModel';
import {
	GraphicActionType,
	GraphicDirectoryInfo,
	GraphicDirectoryInfoPagination,
	GraphicStatus,
	GraphicStudioFilterBy,
	GraphicType,
	MapStudioSortOptions,
} from '@/model/GraphicModel';
import { useStoreDispatch, useStoreSelector } from '@/store';
import { setLoading } from '@/store/common/actions';
import { setGraphicActionType, setMapHasChangedAction } from '@/store/graphic/actions';
import { graphicActionTypeSelector } from '@/store/graphic/selectors';
import { AppstoreFilled, CaretDownFilled, DeleteOutlined, FilterOutlined, PlusOutlined, SortAscendingOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, MenuProps, Popover, Radio, Space, Spin, Upload } from 'antd';
import Search from 'antd/es/input/Search';
import { Content } from 'antd/es/layout/layout';
import { RadioChangeEvent } from 'antd/lib';
import cx from 'classnames';
import debounce from 'lodash.debounce';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { batch } from 'react-redux';
import { GraphicDirectoryCardView } from './GraphicCardView/GraphicDirectoryCardView';
import styles from './graphicDirectory.module.scss';
import { GraphicFilterByContent } from './GraphicFilterByContent/GraphicFilterByContent';
import { GraphicDirectoryListView } from './GraphicListView/GraphicDirectoryListView';
import { GraphicSortByContent } from './GraphicSortByContent/GraphicSortByContent';

type Props = {
	isModal: boolean;
};
const { Group: RadioGroup, Button: RadioButton } = Radio;

const GraphicDirectory: FC<Props> = WithNewGraphicModal(({ isModal, uploadProps, handleOnOpen: handleOnOpenMap, handleOnOpenNewModal }) => {
	//#region state management variables
	const dispatch = useStoreDispatch();
	const graphicActionType: GraphicActionType = useStoreSelector<GraphicActionType>(graphicActionTypeSelector);
	const [searchText, setSearchText] = useState<string>('');
	const [pagination, setPagination] = useState<PaginationSetting>({
		...getDefaultPaginationSettings(),
		SortField: getEnumNameFromValue(MapStudioSortOptions, MapStudioSortOptions.Name),
		SortDirection: SortDirections.Ascend,
		PageSize: 12,
	});
	const [currentView, setCurrentView] = useState<ViewType>(ViewType.Card);
	const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [mapDirectoryData, setMapDirectoryData] = useState<GraphicDirectoryInfo[]>([]);
	const [operatorsDetails, setOperatorsDetails] = useState<OperatorsDetail[]>([]);
	const [filterByData, setFilterByData] = useState<GraphicStudioFilterBy>({ OperatorNames: [], Status: GraphicStatus.Published });
	const [open, setOpen] = useState(false);
	const [sortOpen, setSortOpen] = useState(false);
	const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.Ascend);
	const [sortField, setSortField] = useState<MapStudioSortOptions>(MapStudioSortOptions.Name);
	const [activeOperatorFilter, setActiveOperatorFilter] = useState<boolean>(false);
	const [activeSortFilter, setActiveSortFilter] = useState<boolean>(false);
	const [fileTypeFilterMenu, setFileTypeFilterMenu] = useState<GraphicType>(GraphicType.Web);
	//#endregion

	useEffect(() => {
		setIsLoading(true);
		fetchAndSetMapDirectory(fileTypeFilterMenu, pagination, filterByData);
	}, []);

	useEffect(() => {
		fetchAndSetMapDirectory(fileTypeFilterMenu, pagination, filterByData);

		graphicApi.getOperatorsDetails().then((response: ResponseObjectEntity<OperatorsDetail[]>) => {
			if (!handleResponse(response)) {
				setOperatorsDetails(response.Entity);
			}
		});
	}, []);

	const fetchAndSetMapDirectory = (graphicType: GraphicType, paginationSettings: PaginationSetting, filterData?: GraphicStudioFilterBy) => {
		batch(() => {
			dispatch(setMapHasChangedAction(false));
		});

		graphicApi
			.getMapDirectoryBy({
				GraphicType: graphicType,
				PaginationSetting: paginationSettings,
				OperatorsIdFilters: filterData?.OperatorNames?.map(item => item.Id),
				Status: filterData?.Status,
			})
			.then((mapsResponse: ResponseObjectEntity<GraphicDirectoryInfoPagination>) => {
				if (!handleResponse(mapsResponse)) {
					batch(() => {
						setPagination({
							...paginationSettings,
							TotalItems: mapsResponse.Entity.TotalItemsPaginated,
						});
						setMapDirectoryData(mapsResponse.Entity.MapDirectoriesInfo);
					});
				}
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const callSearch = useCallback(
		debounce(async (v: string) => {
			fetchAndSetMapDirectory(fileTypeFilterMenu, { ...pagination, TotalItems: 0, PageNumber: 1, SearchedValue: v }, filterByData);
		}, 500),
		[pagination?.SearchedValue, fileTypeFilterMenu]
	);

	const handleOnSetSearchText = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.currentTarget;
		setSearchText(removeCommasAndPercentSign(value));
		callSearch(value);
	};

	const handleOnChangeView = ({ target: { value } }: RadioChangeEvent) => {
		const tempPagination: PaginationSetting = {
			...pagination,
			PageSize: value === ViewType.List ? 10 : 12,
			PageNumber: 1,
			SortField: 'Name',
			SortDirection: SortDirections.Ascend,
		};

		setPagination(tempPagination);
		setCurrentView(value);
	};

	const onSortBy = () => {
		handleSortOpenChange(false);
		setActiveSortFilter(true);
		const newPagination: PaginationSetting = {
			...pagination,
			SortField: getEnumNameFromValue(MapStudioSortOptions, MapStudioSortOptions.Name),
			SortDirection: sortDirection,
		};
		fetchAndSetMapDirectory(fileTypeFilterMenu, newPagination, filterByData);
	};

	const onClearSortBy = () => {
		setLoading(false);
		setSortField(MapStudioSortOptions.Name);
		setSortDirection(SortDirections.Ascend);
		const newPagination = {
			...pagination,
			SortField: getEnumNameFromValue(MapStudioSortOptions, MapStudioSortOptions.Name),
			SortDirection: SortDirections.Ascend,
		};
		if (activeSortFilter) {
			handleSortOpenChange(false);
			setActiveSortFilter(false);
		}
		fetchAndSetMapDirectory(GraphicType.Web, newPagination, filterByData);
	};

	const handleSortByOptionChanged = (e: RadioChangeEvent) => {
		const value: MapStudioSortOptions = e.target.value;
		setSortField(value);
	};

	const handleSortDirectionOptionChanged = (e: RadioChangeEvent) => {
		const value: SortDirections = e.target.value;
		setSortDirection(value);
	};

	const handleOnFilterByOperators = () => {
		setActiveOperatorFilter(true);
		fetchAndSetMapDirectory(fileTypeFilterMenu, pagination, filterByData);
	};

	const importMenuItems: MenuProps['items'] = [
		{
			key: '1-import-menu',
			label: (
				<Upload {...uploadProps}>
					<span>{_('ComputerFile')}</span>
				</Upload>
			),
		},
		{
			key: '2-import-menu',
			label: _('FromServer'),
		},
	];

	const importMenu: MenuProps = {
		items: importMenuItems,
		onClick: e => {
			if (e.key.toString() !== '1-import-menu') {
				dispatch(setGraphicActionType(graphicActionType | GraphicActionType.ModalDirectory));
			}
		},
	};

	const handleOpenChange = (newOpen: boolean) => {
		setOpen(newOpen);
	};

	const handleSortOpenChange = (newOpen: boolean) => {
		setSortOpen(newOpen);
	};

	return (
		<div className={cx(styles.modalContainer)}>
			<Layout className={styles.layout}>
				<Content className={styles.content}>
					<div className={styles.toolBar}>
						<div className={styles.items}>
							<Search
								allowClear
								className={styles.searchBar}
								maxLength={50}
								placeholder={_('SearchByName')}
								onChange={handleOnSetSearchText}
								value={searchText}
								enterButton
							/>
							<RadioGroup value={currentView} onChange={handleOnChangeView} buttonStyle='solid' className={styles.switchView}>
								<RadioButton value={ViewType.Card}>
									<AppstoreFilled className={styles.switchViewIcon} />
								</RadioButton>
								<RadioButton value={ViewType.List}>
									<UnorderedListOutlined className={styles.switchViewIcon} />
								</RadioButton>
							</RadioGroup>
							{currentView === ViewType.Card && (
								<Popover
									content={
										<GraphicSortByContent
											handleSortByOptionChanged={handleSortByOptionChanged}
											handleSortDirectionOptionChanged={handleSortDirectionOptionChanged}
											onClearSortBy={onClearSortBy}
											onSortBy={onSortBy}
											sortDirection={sortDirection}
											sortField={sortField}
										/>
									}
									placement='bottom'
									open={sortOpen}
									onOpenChange={handleSortOpenChange}>
									<Button icon={<SortAscendingOutlined />} className={styles.popOverButton} type={activeSortFilter ? 'primary' : 'default'}>
										<span>{_('SortBy')}</span>
										<CaretDownFilled className={styles.popOverDownArrow} />
									</Button>
								</Popover>
							)}
							<Popover
								content={
									<GraphicFilterByContent
										filterByData={filterByData}
										operators={operatorsDetails}
										setFilterByData={setFilterByData}
										onClearFilterBy={() => {
											setLoading(true);
											setFilterByData({ OperatorNames: [], Status: GraphicStatus.Published });
											setActiveOperatorFilter(false);
											fetchAndSetMapDirectory(GraphicType.Web, pagination, filterByData);
										}}
										onFilterBy={handleOnFilterByOperators}
									/>
								}
								open={open}
								placement='bottom'
								onOpenChange={handleOpenChange}>
								<Button icon={<FilterOutlined />} className={styles.popOverButton} type={activeOperatorFilter ? 'primary' : 'default'}>
									<span>{_('FilterBy')}</span>
									<CaretDownFilled className={styles.popOverDownArrow} />
								</Button>
							</Popover>
						</div>
						<div className={styles.addMapsContainer}>
							<Dropdown menu={importMenu}>
								<Button>
									<Space>
										<span>{_('ImportMap')}</span>
										<CaretDownFilled className={styles.popOverDownArrow} />
									</Space>
								</Button>
							</Dropdown>
							<Button type='primary' onClick={handleOnOpenNewModal} icon={<PlusOutlined />}>
								{_('NewMap')}
							</Button>
						</div>
					</div>
					<div>
						<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='large'>
							{currentView === ViewType.Card && (
								<GraphicDirectoryCardView
									handleOnOpenMap={handleOnOpenMap}
									fetchAndSetMapDirectory={fetchAndSetMapDirectory}
									setPagination={setPagination}
									pagination={pagination}
									mapData={mapDirectoryData}
								/>
							)}
							{currentView === ViewType.List && (
								<GraphicDirectoryListView
									fetchAndSetMapDirectory={fetchAndSetMapDirectory}
									handleOnOpenMap={handleOnOpenMap}
									handleOnOpenNewModal={handleOnOpenNewModal}
									pagination={pagination}
									mapData={mapDirectoryData}
									isModal={isModal}
								/>
							)}
						</Spin>
					</div>
					<Button
						icon={<DeleteOutlined />}
						onClick={() => {
							setShowArchiveModal(true);
						}}>
						{_('Archive')}
					</Button>
					{showArchiveModal && (
						<ArchiveModal
							onConfirm={() => {}}
							onCancel={() => {
								setShowArchiveModal(false);
							}}
						/>
					)}
				</Content>
			</Layout>
		</div>
	);
});

export { GraphicDirectory };
